import React, { useState, useRef, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { ChevronsUpDown, Check, X, Search } from "lucide-react";
import { useConsultationsData } from "@/lib/settings/ConsultationsData";

export default function LinkedDocumentSelector({
  value,
  onChange,
}: {
  value?: string;
  onChange: (id: string | undefined) => void;
}) {
  const { allCompanyDocuments } = useConsultationsData();
  const [open, setOpen] = useState(false);
  const [search, setSearch] = useState("");
  const containerRef = useRef<HTMLDivElement>(null);

const documents =  allCompanyDocuments;


const selected = value ? documents?.find((d) => d._id === value) : undefined;
const filteredDocs = documents?.filter((d) =>
  d.title?.toLowerCase().includes(search.toLowerCase())
);

  // close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  return (
    <div ref={containerRef} className="relative flex items-center gap-2 w-full">
      {/* Trigger button */}
      <div className="relative w-full md:w-[400px]">
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          aria-label="Select linked document"
          className="justify-between w-full h-8 text-sm"
          onClick={() => setOpen(!open)}
        >
          {selected ? selected.title : "Select document..."}
          <ChevronsUpDown className="ml-2 h-4 w-4 opacity-50" />
        </Button>

        {/* Dropdown */}
        {open && (
          <div
            className="absolute left-0 mt-1 w-full bg-white border border-gray-200 rounded-md shadow-lg z-[9999] p-2"
          >
            {/* Search bar */}
            <div className="flex items-center border border-gray-300 rounded-sm px-2 py-1 mb-2">
              <Search className="h-4 w-4 text-gray-400 mr-2" />
              <input
                type="text"
                placeholder="Search documents..."
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                className="w-full text-sm focus:outline-none"
              />
            </div>

            {/* Results list */}
            <div className="max-h-60 overflow-y-auto">
              {filteredDocs?.length > 0 ? (
                filteredDocs?.map((doc) => (
                  <div
                    key={doc._id}
                    className={`flex items-center px-2 py-1 text-sm cursor-pointer hover:bg-gray-100 rounded ${
                      value === doc._id ? "bg-gray-100" : ""
                    }`}
                    onClick={() => {
                      onChange(value === doc._id ? undefined : doc._id);
                      setOpen(false);
                    }}
                  >
                    <Check
                      className={`mr-2 h-4 w-4 ${
                        value === doc._id ? "opacity-100" : "opacity-0"
                      }`}
                    />
                    <span className="truncate">{doc.title}</span>
                    {/* <span className="ml-auto text-xs text-gray-500">
                      {doc.folder || doc.type}
                    </span> */}
                  </div>
                ))
              ) : (
                <div className="text-sm text-gray-500 px-2 py-1">
                  No document found.
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      {/* Clear button */}
      {value && (
        <Button
          variant="ghost"
          size="icon"
          className="h-8 w-8"
          title="Clear selection"
          onClick={() => onChange(undefined)}
        >
          <X className="h-4 w-4" />
        </Button>
      )}
    </div>
  );
}
